import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, FileUp, Send } from 'lucide-react';
import { masterImportAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';

const pretty = (value) => JSON.stringify(value, null, 2);

const AdminMasterImportPage = ({ masterType, backPath = '/admin' }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [file, setFile] = useState(null);
  const [spec, setSpec] = useState(null);
  const [jsonPayload, setJsonPayload] = useState('[]');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await masterImportAPI.getSpec(masterType);
        const s = res.data;
        setSpec(s);
        setJsonPayload(pretty(s.sample_api_payload || []));
      } catch (error) {
        toast.error(getErrorMessage(error, 'Failed to load import spec'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [masterType]);

  const endpointInfo = useMemo(() => ({
    upload: `/api/master-import/${masterType}/upload`,
    json: `/api/master-import/${masterType}/json`,
    sampleCsv: `/api/master-import/specs/${masterType}/sample.csv`,
  }), [masterType]);

  const handleDownloadSample = async () => {
    try {
      const response = await masterImportAPI.downloadSampleCsv(masterType);
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${masterType}_sample.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Sample CSV downloaded');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to download sample CSV'));
    }
  };

  const handleUploadImport = async () => {
    if (!file) {
      toast.error('Please choose a file');
      return;
    }
    try {
      setSubmitting(true);
      const res = await masterImportAPI.importUpload(masterType, file);
      const data = res.data || {};
      toast.success(`Upload import done: created ${data.created || 0}, updated ${data.updated || 0}, failed ${data.failed || 0}`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'File import failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleJsonImport = async () => {
    try {
      const parsed = JSON.parse(jsonPayload);
      if (!Array.isArray(parsed)) {
        toast.error('JSON payload must be an array of objects');
        return;
      }
      setSubmitting(true);
      const res = await masterImportAPI.importJson(masterType, parsed);
      const data = res.data || {};
      toast.success(`JSON import done: created ${data.created || 0}, updated ${data.updated || 0}, failed ${data.failed || 0}`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'JSON import failed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!spec) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">Import spec not found for `{masterType}`.</p>
          <Button className="mt-4" onClick={() => navigate(backPath)}>Back</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Import {spec.title}</h2>
          <p className="text-sm text-muted-foreground">Bulk import via file upload or API JSON payload</p>
        </div>
        <Button variant="outline" onClick={() => navigate(backPath)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
      </div>

      <Card>
        <CardHeader><CardTitle>Instructions</CardTitle></CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p><strong>Required fields:</strong> {(spec.required_columns || []).join(', ') || 'None'}</p>
          <p><strong>Optional fields:</strong> {(spec.optional_columns || []).join(', ') || 'None'}</p>
          <p><strong>File import endpoint:</strong> <code>{endpointInfo.upload}</code></p>
          <p><strong>API JSON endpoint:</strong> <code>{endpointInfo.json}</code></p>
          <p><strong>Sample CSV endpoint:</strong> <code>{endpointInfo.sampleCsv}</code></p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleDownloadSample}>
              <Download className="h-4 w-4 mr-2" />
              Download Sample CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Import From Excel / CSV</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Label>Upload File (.csv, .xlsx, .xls)</Label>
          <Input type="file" accept=".csv,.xlsx,.xls" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <Button onClick={handleUploadImport} disabled={submitting}>
            <FileUp className="h-4 w-4 mr-2" />
            Import File
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Import Through API Payload (JSON)</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Label>Sample JSON Payload</Label>
          <Textarea className="min-h-[280px] font-mono text-xs" value={jsonPayload} onChange={(e) => setJsonPayload(e.target.value)} />
          <Button onClick={handleJsonImport} disabled={submitting}>
            <Send className="h-4 w-4 mr-2" />
            Import JSON
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminMasterImportPage;
